import React, { useState } from "react";
import { Check, Minus, Sparkles, ArrowRight, ChevronDown, ChevronUp } from "lucide-react";
import { motion, AnimatePresence } from "motion/react";
import {
  AUDITED_PLAN_FEATURES,
  PLAN_CATEGORIES,
  PlanFeature,
  FeatureCategory
} from "../config/planFeatures";

interface PricingComparisonTableProps {
  settingsAppTheme?: string;
  onUpgradeClick?: () => void;
  onLoginClick?: () => void;
  onNavigate?: (path: string) => void;
}

export function PricingComparisonTable({
  settingsAppTheme = "light",
  onUpgradeClick,
  onLoginClick
}: PricingComparisonTableProps) {
  const isDark = settingsAppTheme === "dark";
  const [isExpanded, setIsExpanded] = useState(false);

  // Primary features (high impact highlights for the main 7-10 row table)
  const primaryFeatures = AUDITED_PLAN_FEATURES.filter((f) => f.highlight);

  // Secondary features for the expandable detailed breakdown
  const secondaryFeatures = AUDITED_PLAN_FEATURES.filter((f) => !f.highlight);

  // Group secondary features by category
  const secondaryGrouped = PLAN_CATEGORIES.map((category) => ({
    category,
    features: secondaryFeatures.filter((f) => f.category === category)
  })).filter((group) => group.features.length > 0);

  const renderValue = (val: string | boolean, isProColumn = false) => {
    if (typeof val === "boolean") {
      if (val) {
        return (
          <span className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400 font-extrabold text-xs">
            <Check className="w-4 h-4 stroke-[3]" />
            <span>Included</span>
          </span>
        );
      }
      return (
        <span className="inline-flex items-center text-slate-300 dark:text-slate-600 font-medium text-xs">
          <Minus className="w-4 h-4" />
        </span>
      );
    }

    const isHighlightVal =
      val.toLowerCase().includes("unlimited") ||
      val.toLowerCase().includes("expanded") ||
      val.toLowerCase().includes("nearest-first") ||
      val.toLowerCase().includes("live technical") ||
      val.toLowerCase().includes("visual calendar");

    return (
      <span
        className={`inline-block px-2.5 py-1 rounded-lg text-xs font-extrabold text-center transition-all ${
          isHighlightVal && isProColumn
            ? "bg-indigo-100 text-indigo-900 dark:bg-indigo-950/90 dark:text-indigo-200 border border-indigo-200 dark:border-indigo-800"
            : isProColumn
            ? "bg-indigo-50 text-indigo-800 dark:bg-indigo-950/50 dark:text-indigo-300 border border-indigo-100 dark:border-indigo-900"
            : "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300 border border-slate-200/80 dark:border-slate-700"
        }`}
      >
        {val}
      </span>
    );
  };

  const renderDesktopRow = (item: PlanFeature) => (
    <div
      key={item.id}
      className={`grid grid-cols-12 items-center transition ${
        item.highlight
          ? isDark
            ? "bg-indigo-950/10 hover:bg-indigo-950/20"
            : "bg-indigo-50/20 hover:bg-indigo-50/40"
          : isDark
          ? "hover:bg-slate-800/30"
          : "hover:bg-slate-50/80"
      }`}
    >
      <div className="col-span-6 p-4 text-left">
        <div className="font-bold text-xs md:text-sm text-slate-800 dark:text-slate-200 flex items-center gap-2">
          <span>{item.feature}</span>
        </div>
        <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5 font-medium leading-normal">
          {item.description}
        </p>
      </div>

      <div className="col-span-3 p-4 text-center border-l border-slate-100 dark:border-slate-800/80 flex items-center justify-center">
        {renderValue(item.free, false)}
      </div>

      <div
        className={`col-span-3 p-4 text-center border-l flex items-center justify-center ${
          isDark ? "bg-indigo-950/20 border-indigo-900/40" : "bg-indigo-50/30 border-indigo-100/60"
        }`}
      >
        {renderValue(item.pro, true)}
      </div>
    </div>
  );

  const renderMobileCard = (item: PlanFeature) => (
    <div
      key={item.id}
      className={`p-3.5 rounded-xl border transition ${
        item.highlight
          ? isDark
            ? "bg-slate-900/90 border-indigo-900/50"
            : "bg-white border-indigo-100 shadow-2xs"
          : isDark
          ? "bg-slate-900 border-slate-800"
          : "bg-white border-slate-200 shadow-2xs"
      }`}
    >
      <div className="mb-2">
        <h5 className={`font-bold text-xs ${isDark ? "text-slate-100" : "text-slate-900"}`}>
          {item.feature}
        </h5>
        <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5 font-medium leading-snug">
          {item.description}
        </p>
      </div>

      <div className="grid grid-cols-2 gap-2 pt-2 border-t border-slate-100 dark:border-slate-800 text-center">
        <div
          className={`p-2 rounded-lg border ${
            isDark ? "bg-slate-800/60 border-slate-700/60" : "bg-slate-50 border-slate-200/80"
          }`}
        >
          <span className="block text-[10px] uppercase font-extrabold text-slate-400 dark:text-slate-500 mb-1">
            Free
          </span>
          {renderValue(item.free, false)}
        </div>

        <div
          className={`p-2 rounded-lg border relative ${
            isDark
              ? "bg-indigo-950/40 border-indigo-900/80"
              : "bg-indigo-50/70 border-indigo-100"
          }`}
        >
          <span className="block text-[10px] uppercase font-extrabold text-indigo-600 dark:text-indigo-400 mb-1">
            BizNearYou Pro
          </span>
          {renderValue(item.pro, true)}
        </div>
      </div>
    </div>
  );

  return (
    <div className="w-full max-w-5xl mx-auto my-6 font-sans select-none">
      {/* Intro Header */}
      <div className="text-center mb-6 space-y-2">
        <h3 className={`text-xl md:text-2xl font-black tracking-tight ${isDark ? "text-white" : "text-slate-900"}`}>
          Compare Plans Side-by-Side
        </h3>
        <p className={`text-xs md:text-sm ${isDark ? "text-slate-400" : "text-slate-600"} max-w-2xl mx-auto font-medium`}>
          See the core differences between Free and Pro, verified against our live application capabilities.
        </p>
      </div>

      {/* ========================================================= */}
      {/* 1. DESKTOP VIEW                                           */}
      {/* ========================================================= */}
      <div className={`hidden md:block rounded-2xl border shadow-lg overflow-hidden transition-all ${
        isDark ? "bg-slate-900 border-slate-800 shadow-black/40" : "bg-white border-slate-200 shadow-slate-200/50"
      }`}>
        {/* Desktop Header Row (NO prices) */}
        <div className={`grid grid-cols-12 border-b items-center transition ${
          isDark ? "bg-slate-850 border-slate-800 text-slate-100" : "bg-slate-50 border-slate-200 text-slate-900"
        }`}>
          <div className="col-span-6 p-4 font-black text-xs uppercase tracking-wider text-slate-500 dark:text-slate-400">
            Features
          </div>
          <div className="col-span-3 p-4 text-center font-black text-sm border-l border-slate-200 dark:border-slate-800 text-slate-800 dark:text-slate-200">
            Free
          </div>
          <div className={`col-span-3 p-4 text-center font-black text-sm border-l relative ${
            isDark ? "bg-indigo-950/40 border-indigo-900/60 text-indigo-300" : "bg-indigo-50/60 border-indigo-100 text-indigo-700"
          }`}>
            <span className="absolute -top-0 left-1/2 -translate-x-1/2 bg-indigo-600 text-white text-[9px] uppercase font-black tracking-widest px-2.5 py-0.5 rounded-b-md">
              Recommended
            </span>
            <div className="flex items-center justify-center gap-1.5 pt-1">
              <Sparkles className="w-3.5 h-3.5 fill-current text-amber-400" />
              <span>BizNearYou Pro</span>
            </div>
          </div>
        </div>

        {/* Primary Desktop Rows */}
        <div className="divide-y divide-slate-100 dark:divide-slate-800/80">
          {primaryFeatures.map((item) => renderDesktopRow(item))}
        </div>

        {/* Expanded Desktop Details */}
        <AnimatePresence>
          {isExpanded && (
            <motion.div
              id="expanded-plan-details-desktop"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.25, ease: "easeInOut" }}
              className="overflow-hidden border-t border-slate-200 dark:border-slate-800 divide-y divide-slate-100 dark:divide-slate-800/80"
            >
              {secondaryGrouped.map((group) => (
                <div key={group.category} className="divide-y divide-slate-100 dark:divide-slate-800/80">
                  <div className={`p-3 px-6 text-xs font-black uppercase tracking-wider ${
                    isDark ? "bg-slate-800/90 text-indigo-400" : "bg-slate-100/90 text-indigo-700"
                  }`}>
                    {group.category}
                  </div>
                  {group.features.map((item) => renderDesktopRow(item))}
                </div>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* ========================================================= */}
      {/* 2. MOBILE VIEW                                            */}
      {/* ========================================================= */}
      <div className="block md:hidden space-y-3">
        {/* Mobile Header Intro */}
        <div className={`p-3.5 rounded-xl border text-center ${
          isDark ? "bg-slate-900 border-slate-800" : "bg-slate-50 border-slate-200"
        }`}>
          <h4 className={`font-black text-sm ${isDark ? "text-white" : "text-slate-900"}`}>
            Compare Free & Pro
          </h4>
          <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">
            Key differences between plan tiers at a glance.
          </p>
        </div>

        {/* Primary Mobile Cards */}
        <div className="space-y-2.5">
          {primaryFeatures.map((item) => renderMobileCard(item))}
        </div>

        {/* Expanded Mobile Details */}
        <AnimatePresence>
          {isExpanded && (
            <motion.div
              id="expanded-plan-details-mobile"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.25, ease: "easeInOut" }}
              className="overflow-hidden space-y-3 pt-1"
            >
              {secondaryGrouped.map((group) => (
                <div key={group.category} className="space-y-2">
                  <div className={`p-2.5 rounded-lg text-xs font-black uppercase tracking-wider text-center ${
                    isDark ? "bg-slate-800 text-indigo-400" : "bg-slate-100 text-indigo-700"
                  }`}>
                    {group.category}
                  </div>
                  <div className="space-y-2.5">
                    {group.features.map((item) => renderMobileCard(item))}
                  </div>
                </div>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* ========================================================= */}
      {/* 3. EXPAND / COLLAPSE TOGGLE BUTTON                        */}
      {/* ========================================================= */}
      {secondaryFeatures.length > 0 && (
        <div className="my-5 text-center">
          <button
            type="button"
            onClick={() => setIsExpanded(!isExpanded)}
            aria-expanded={isExpanded}
            aria-controls="expanded-plan-details-desktop expanded-plan-details-mobile"
            className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-xs font-extrabold transition cursor-pointer border focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${
              isDark
                ? "bg-slate-800 hover:bg-slate-750 text-slate-200 border-slate-700"
                : "bg-white hover:bg-slate-50 text-slate-800 border-slate-300 shadow-2xs"
            }`}
          >
            <span>{isExpanded ? "Hide plan details" : "View all plan details"}</span>
            {isExpanded ? (
              <ChevronUp className="w-4 h-4 text-indigo-500" />
            ) : (
              <ChevronDown className="w-4 h-4 text-indigo-500" />
            )}
          </button>
        </div>
      )}
    </div>
  );
}
